/**
 * Workshops Page
 * Lists all available workshops
 */

import { Link } from "react-router-dom";
import { WorkshopCard } from "@components/workshop";
import { useUserSettings } from "@hooks/useUserSettings";
import { useWorkshops, useExercises, useMyProgress } from "@hooks";
import type { Workshop, Exercise, Progress } from "@/types";

export function WorkshopsPage() {
  const { settings } = useUserSettings();
  const { data: workshopsData, isLoading: workshopsLoading } = useWorkshops({
    status: "active",
  });
  const { data: exercises = [] } = useExercises();
  const { data: progress = [] } = useMyProgress();

  const workshops = workshopsData ?? [];

  // Calculate progress for each workshop
  const getWorkshopProgress = (workshopId: string) => {
    const workshopExercises = exercises.filter(
      (e: Exercise) => e.workshop_id === workshopId,
    );
    const completedCount = progress.filter(
      (p: Progress) =>
        p.status === "completed" &&
        workshopExercises.some((e: Exercise) => e.id === p.exercise_id),
    ).length;
    return {
      completed: completedCount,
      total: workshopExercises.length,
    };
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
          <Link
            to="/"
            className="hover:text-primary-600 dark:hover:text-primary-400"
          >
            Dashboard
          </Link>
          <span>/</span>
          <span>Workshops</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Workshops
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Browse and complete training workshops to master AI agent development.
        </p>
      </div>

      {/* Setup Wizard Banner - shown for users who haven't completed setup */}
      {!settings.setupCompleted && (
        <section
          aria-labelledby="setup-wizard-heading"
          className="mb-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3
                id="setup-wizard-heading"
                className="text-lg font-semibold text-gray-900 dark:text-white"
              >
                Set Up Visual Builder
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Configure the ADK Visual Builder to practice building agents
                locally. This is optional but recommended for hands-on workshop
                exercises.
              </p>
            </div>
            <Link
              to="/getting-started"
              className="flex-shrink-0 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500 text-white font-medium rounded-lg transition-colors inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Setup Wizard
            </Link>
          </div>
        </section>
      )}

      {/* Workshop Grid */}
      {workshopsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 animate-pulse"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="w-16 h-6 bg-gray-200 dark:bg-gray-700 rounded-full" />
              </div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-1" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-4" />
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full" />
            </div>
          ))}
        </div>
      ) : workshops.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Workshops Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Check back later for new workshops or contact your administrator.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workshops.map((workshop: Workshop) => (
            <WorkshopCard
              key={workshop.id}
              workshop={workshop}
              progress={getWorkshopProgress(workshop.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
